import { calculateCoverageFee } from '../components/quoting/powerups/utils';
import { DISCOUNT_MODIFIER_TYPE, PetType } from '../constants';
import {
  apiCall,
  doNothing,
  getAgeInYears,
  toBoolean,
} from '../util';
import { logError } from './common';
import { INITIAL_EMAIL_REASIGNED } from './types';

async function callFSCLeadWithErrors({ url, options, origin, dispatch }) {
  let responseFSC;
  let payloadFSC;
  try {
    responseFSC = await apiCall(url, options);
    if (responseFSC?.status !== 200 && responseFSC?.status !== 202) {
      payloadFSC = await responseFSC.json();
      throw new Error(payloadFSC?.title || 'Something went wrong');
    }
  } catch (exception) {
    dispatch(logError({
      action: origin,
      description: `FSC Leads - ${exception.toString()}`,
      endpoint: url,
      exception,
      request: options?.post,
      response: payloadFSC,
      statusCode: responseFSC?.status,
    }));
  }
}

/**
 * Send lead with pet data, used on quote rate created
 * @param {Object} pet - The first pet when quote is created
 */
export function sendFSCLead({
  age = 0,
  breed = '',
  breedId = 0,
  email = '',
  gender = '',
  marketingOpt = false,
  petName = '',
  petType = '',
  promoCode,
  state,
  zipCode = '',
}) {
  return async (dispatch, getState) => {
    const { quoting: { saveQuote: { quoteId } } } = getState();
    const url = 'purchase/FSCLead';
    const body = {
      age,
      breed,
      breedId,
      email,
      gender,
      marketingOpt,
      petName,
      petType,
      promoCode,
      quoteId,
      quoteType: 'EB',
      state,
      zipCode,
    };
    let response;
    let payload;

    try {
      let stateAbbr = state;
      if (!state) {
        const stateValidationUrl =
          `purchase/State/GetStateByZipCode/${zipCode}`;
        response = await apiCall(stateValidationUrl);
        payload = await response.json();
        stateAbbr = payload?.Data?.StateAbbr || '';
      }
      await callFSCLeadWithErrors({
        dispatch,
        options: {
          post: {
            ...body,
            state: stateAbbr,
          },
        },
        origin: 'sendFSCLead',
        url,
      });
    } catch (exception) {
      dispatch(logError({
        action: 'sendFSCLead',
        description: `FSC Leads - ${exception.toString()}`,
        endpoint: url,
        exception,
        response: payload,
        statusCode: response?.status,
      }));
    }
  };
}

/** Send leads for each pet data
*/
export function sendAllPetsFSCLead() {
  return async (dispatch, getState) => {
    const {
      quoting: {
        data,
        newCustomer,
        parameters,
        customerZipCode,
      },
    } = getState();
    if (data) {
      data?.ebPetQuoteResponseList.forEach((pet) => {
        dispatch(sendFSCLead({
          age: Number(getAgeInYears(pet.petAgeName)),
          breed: pet.breedName,
          breedId: pet.breedId,
          email: newCustomer.email || parameters.email,
          gender: pet.genderName,
          marketingOpt: toBoolean(parameters.marketingOptIn),
          petName: pet.petName,
          petType: PetType.Dog.value === pet.petType
            ? PetType.Dog.name : PetType.Cat.name,
          promoCode: data?.groupCode || '',
          state: data?.stateAbrv || newCustomer.address.state || '',
          zipCode: customerZipCode
            || newCustomer.address.zipCode
            || parameters.petZipCode,
        }));
      });
    }
  };
}

/**
 * Send lead with customer data, used after filling the Your info form
 * @param {Object} customer - The customer and secondary parent info
 */
export function sendFSCCustomerLead() {
  return async (dispatch, getState) => {
    dispatch({ type: INITIAL_EMAIL_REASIGNED });
    const {
      quoting: {
        quoteSelectedValues,
        saveQuote: { quoteId },
        newCustomer,
      },
    } = getState();
    const url = 'purchase/FSCLead/customer';
    const body = {
      address: {
        city: newCustomer.address.city,
        line1: newCustomer.address.street,
        line2: newCustomer.address.street2,
        state: newCustomer.address.state,
        zip: newCustomer.address.zipCode,
      },
      email: newCustomer.email,
      firstName: newCustomer.name,
      lastName: newCustomer.lastName,
      pets: quoteSelectedValues.map((pet) => ({ petName: pet.petName })),
      phoneNumber: newCustomer.phoneNumber,
      quoteId,
      secondaryPetParent: {
        firstName: newCustomer.secondParent.name,
        lastName: newCustomer.secondParent.lastName,
        phoneNumber: newCustomer.secondParent.phoneNumber,
      },
    };

    try {
      callFSCLeadWithErrors({
        dispatch,
        options: { post: body },
        origin: 'sendFSCCustomerLead',
        url,
      });
    } catch (error) {
      doNothing();
    }
  };
}

const getModifierData = ({ modifierId, quotingState, quoteId }) => {
  const {
    data,
    quoteSelectedValues,
  } = quotingState;

  const { ebPetQuoteResponseList } = data;

  const petQuote = ebPetQuoteResponseList
    .find((item) => item.petQuoteId === quoteId);
  const quoteSelected = quoteSelectedValues
    .find((item) => item.quoteId === quoteId)
    || { petName: 'Pet Name' };

  if (!petQuote || !quoteSelected) {
    return null;
  }

  const powerUp = petQuote.InsuranceModifiersEB
    .find((modifier) => {
      if (modifier.Id !== modifierId
        && modifier?.BundleInsuranceModifiersEB?.length) {
        const bundleModifier = modifier.BundleInsuranceModifiersEB
          .find((bmodifier) => bmodifier.Id === modifierId);
        if (bundleModifier) {
          return bundleModifier;
        }
      }
      return modifier.Id === modifierId;
    });

  if (!powerUp) {
    return null;
  }

  const powerUpPrice = calculateCoverageFee({
    data,
    petQuote,
    petQuoteSelected: quoteId,
    powerUp,
    quoteSelected,
  });

  return {
    amount: Number(powerUpPrice),
    name: powerUp.AppText,
    type: 'Powerup',
  };
};

const getPlanName = ({ selectedPlanPackaged, quoteInfo }) => {
  const planInfo = quoteInfo.PrePackagedPlans
    .find((plan) => plan.Id === selectedPlanPackaged);
  return planInfo?.Name || 'Custom plan';
};

const getTotalDiscount = (allDiscounts) => allDiscounts
  .reduce((accumulator, current) => {
    try {
      const { InsuranceModifierDiscount: { Discount, DiamondName } } = current;
      const percentage = Number(String(Discount)
        .replace('%', '')) || 0;

      const text = accumulator.discountGroup
        ? `${accumulator.discountGroup}, ${DiamondName}`
        : DiamondName;
      return {
        discount: accumulator.discount + percentage,
        discountGroup: text,
      };
    } catch (e) {
      return accumulator;
    }
  }, { discount: 0, discountGroup: '' });

const getPetLead = ({
  customerZipCode,
  newCustomer,
  parameters,
  pet,
  stateAbrv,
}) => ({
  age: Number(getAgeInYears(pet.petAgeName)),
  breed: pet.breedName,
  breedId: `${pet.breedId}`,
  gender: pet.genderName,
  petType: PetType.Dog.value === pet.petType
    ? PetType.Dog.name : PetType.Cat.name,
  state: stateAbrv || newCustomer.address.state || '',
  zipCode: customerZipCode
    || newCustomer.address.zipCode
    || parameters.petZipCode,
});
/**
 * Send lead with quotes data, used once when user finish to customize quote
 */
export function sendFSCQuoteLead() {
  return async (dispatch, getState) => {
    const url = 'purchase/FSCLead/quote';
    const { quoting } = getState();
    try {
      const {
        data: { groupCode, ebPetQuoteResponseList, stateAbrv },
        quoteSelectedValues,
        saveQuote: { quoteId },
        newCustomer,
        parameters,
        customerZipCode,
      } = quoting;

      const quotes = quoteSelectedValues.map((quote) => {
        const petQuote = ebPetQuoteResponseList
          .find((item) => item.petQuoteId === quote.quoteId);

        if (!petQuote) { return null; }

        const modifiers = quote.modifiers
          .filter((modifier) => modifier.isSelected)
          .map((modifier) => getModifierData({
            modifierId: modifier.id,
            quoteId: quote.quoteId,
            quotingState: quoting,
          })).filter((modifier) => Boolean(modifier));

        const discountModifiers = petQuote.InsuranceModifiersEB
          .filter((modifier) => modifier.InsuranceModifierTypeEBId
            === DISCOUNT_MODIFIER_TYPE && modifier.IsSelected);
        const discountResume = getTotalDiscount(discountModifiers);

        const { reimbursementPercentage } = quote;
        const reimbursement = Number(reimbursementPercentage.replace(/%/g, ''));
        const coinsurance = 100 - reimbursement;

        return {
          annualMax: Number(quote.selectedPlanAmount.replace(/\$|,/g, ''))
            || 0,
          Coinsurance: coinsurance,
          deductible: Number(quote.deductibleQuantity.replace(/\$|,/g, '')),
          modifiers,
          petLead: getPetLead({
            customerZipCode,
            newCustomer,
            parameters,
            pet: petQuote,
            stateAbrv,
          }),
          petName: quote.petName,
          planPrice: quote.totalMontly,
          policyPlan: getPlanName({
            quoteInfo: petQuote,
            selectedPlanPackaged: quote.selectedPlanPackaged,
          }),
          quoteType: 'Employee Benefits',
          ...discountResume,
        };
      }).filter((quote) => quote);

      const body = {
        email: quoting.newCustomer.email || quoting.parameters.email,
        lastName: quoting.newCustomer.lastName || '',
        membershipLevel: '',
        membershipNumber: '',
        membershipPartner: '',
        promoCode: groupCode,
        quoteId,
        quotes,
      };
      await callFSCLeadWithErrors({
        dispatch,
        options: { post: body },
        origin: 'sendFSCQuoteLead',
        url,
      });
    } catch (err) {
      doNothing();
    }
  };
}

/**
 * Send lead when potential client updates payment
 */
export function sendFSCPaymentLead() {
  return async (dispatch, getState) => {
    const url = 'purchase/FSCLead/payment';
    const { quoting } = getState();
    try {
      const {
        quoteSelectedValues,
        newCustomer,
        saveQuote: { quoteId },
      } = quoting;

      const body = {
        email: quoting.newCustomer.email || quoting.parameters.email,
        lastName: newCustomer.lastName,
        pets: quoteSelectedValues.map((pet) => ({ petName: pet.petName })),
        quoteId,
      };
      await callFSCLeadWithErrors({
        dispatch,
        options: { post: body },
        origin: 'sendFSCPaymentLead',
        url,
      });
    } catch (error) {
      doNothing();
    }
  };
}

/**
 * Send lead when client completes purchase
 */
export function sendFSCPurchaseLead(successfulPurchases = []) {
  return async (dispatch, getState) => {
    const url = 'purchase/FSCLead/purchase';
    const { quoting } = getState();
    try {
      const {
        newCustomer,
        saveQuote: { quoteId },
      } = quoting;

      const body = {
        email: quoting.newCustomer.email || quoting.parameters.email,
        lastName: newCustomer.lastName,
        pets: successfulPurchases.map((pet) => ({ petName: pet.PetName })),
        quoteId,
      };
      await callFSCLeadWithErrors({
        dispatch,
        options: { post: body },
        origin: 'sendFSCPurchaseLead',
        url,
      });
    } catch (error) {
      doNothing();
    }
  };
}

/**
 * Send lead when client updates the SMS Alerts
 * @param {Object} param
 * @param {string} param.phoneNumber
 */
export function sendFSCMarketingLead({ phoneNumber }) {
  return async (dispatch, getState) => {
    const url = 'purchase/FSCLead/marketing';
    const { quoting } = getState();
    try {
      const {
        newCustomer,
        saveQuote: { quoteId },
        sessionInformation: {
          nopCommerceUser: {
            DiamonClientdId,
          },
        },
      } = quoting;

      const body = {
        customerId: String(DiamonClientdId),
        email: newCustomer.email || quoting.parameters.email,
        lastName: newCustomer.lastName || '',
        quoteId,
        smsOptIn: true,
        smsPhone: phoneNumber,
      };
      await callFSCLeadWithErrors({
        dispatch,
        options: { post: body },
        origin: 'sendFSCMarketingLead',
        url,
      });
    } catch (error) {
      doNothing();
    }
  };
}
